/**
 * Quote library hook — projectTypes + addOns with normalisation,
 * localStorage cache, and silent API refresh.
 *
 * Extracted from useQuoteCalculation to keep the math hook focused.
 * Public shape is intentionally minimal: { projectTypes, setProjectTypes,
 * addOns, setAddOns, refreshLibrary }.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public fallback so the calculator renders a USEFUL set of trades + add-ons
// for unauthenticated visitors AND while the API call is in flight. This
// list mirrors backend `DEFAULT_PROJECT_TYPES` exactly so logged-out and
// logged-in users see the same starter catalog. When the API returns a
// personalized library, this is replaced.
const _FALLBACK_PROJECT_TYPES_RAW = [
  // Painting
  { id: 'interior-paint',    slug: 'interior-paint',    label: 'Interior Painting',    material_rate: 2.5,   labor_hours: 0.15,  measurement_type: 'square_feet', is_default: true },
  { id: 'exterior-paint',    slug: 'exterior-paint',    label: 'Exterior Painting',    material_rate: 3.2,   labor_hours: 0.18,  measurement_type: 'square_feet', is_default: true },
  { id: 'cabinet-resurface', slug: 'cabinet-resurface', label: 'Cabinet Resurfacing',  material_rate: 8.5,   labor_hours: 0.5,   measurement_type: 'square_feet', is_default: true },
  { id: 'drywall-repair',    slug: 'drywall-repair',    label: 'Drywall Repair',       material_rate: 4.0,   labor_hours: 0.25,  measurement_type: 'square_feet', is_default: true },
  { id: 'deck-staining',     slug: 'deck-staining',     label: 'Deck Staining',        material_rate: 3.8,   labor_hours: 0.2,   measurement_type: 'square_feet', is_default: true },
  { id: 'bathtub-resurface', slug: 'bathtub-resurface', label: 'Bathtub Resurface',    material_rate: 12.0,  labor_hours: 0.4,   measurement_type: 'square_feet', is_default: true },
  { id: 'bathtub-enclosure', slug: 'bathtub-enclosure', label: 'Bathtub and Enclosure',material_rate: 15.0,  labor_hours: 0.6,   measurement_type: 'square_feet', is_default: true },
  { id: 'counter-tops',      slug: 'counter-tops',      label: 'Counter Tops',         material_rate: 10.0,  labor_hours: 0.45,  measurement_type: 'linear_feet', is_default: true },
  // Other trades
  { id: 'electrician',       slug: 'electrician',       label: 'Electrical Work',      material_rate: 0.0,   labor_hours: 1.0,   measurement_type: 'hourly',       is_default: true },
  { id: 'plumbing',          slug: 'plumbing',          label: 'Plumbing',             material_rate: 0.0,   labor_hours: 1.0,   measurement_type: 'hourly',       is_default: true },
  { id: 'roofing',           slug: 'roofing',           label: 'Roofing',              material_rate: 4.5,   labor_hours: 0.10,  measurement_type: 'square_feet', is_default: true },
  { id: 'windows',           slug: 'windows',           label: 'Window Installation',  material_rate: 250.0, labor_hours: 1.0,   measurement_type: 'count',       is_default: true },
  { id: 'lawn-care',         slug: 'lawn-care',         label: 'Lawn Care',            material_rate: 35.0,  labor_hours: 1.0,   measurement_type: 'count',       is_default: true },
  { id: 'cleaning',          slug: 'cleaning',          label: 'Cleaning',             material_rate: 0.0,   labor_hours: 1.0,   measurement_type: 'hourly',       is_default: true },
  { id: 'special',           slug: 'special',           label: 'Special / Other',      material_rate: 0.0,   labor_hours: 1.0,   measurement_type: 'hourly',       is_default: true },
  // Custom catch-all
  { id: 'custom',            slug: 'custom',            label: 'Custom Project',       material_rate: 5.0,   labor_hours: 0.2,   measurement_type: 'square_feet', is_default: true },
];

const _FALLBACK_ADD_ONS_RAW = [
  { id: 'primer',          slug: 'primer',          label: 'Primer Application', price: 150.0, is_default: true },
  { id: 'extra-coat',      slug: 'extra-coat',      label: 'Extra Coat',         price: 200.0, is_default: true },
  { id: 'repairs',         slug: 'repairs',         label: 'Minor Repairs',      price: 180.0, is_default: true },
  { id: 'rush',            slug: 'rush',            label: 'Rush Job (48hr)',    price: 300.0, is_default: true },
  { id: 'froth-pak',       slug: 'froth-pak',       label: 'Froth Pak',          price: 250.0, is_default: true },
  { id: 'stripping-paint', slug: 'stripping-paint', label: 'Stripping Paint',    price: 350.0, is_default: true },
  { id: 'clear-coat',      slug: 'clear-coat',      label: 'Clear Coat',         price: 175.0, is_default: true },
];

// Normalise an API project-type row to the legacy shape the calculator expects.
// Exposes both API-snake fields and legacy camelCase fields so consumers keep
// reading `materialRate` / `laborHours` without churn.
export const normalisePT = (pt) => ({
  ...pt,
  value: pt.slug || pt.id,
  materialRate: pt.material_rate,
  laborHours: pt.labor_hours,
  measurementType: pt.measurement_type,
});

export const normaliseAO = (a) => ({ ...a });

export const FALLBACK_PROJECT_TYPES = _FALLBACK_PROJECT_TYPES_RAW.map(normalisePT);
export const FALLBACK_ADD_ONS = _FALLBACK_ADD_ONS_RAW.map(normaliseAO);

// Cache key bumped to v3 to invalidate any stale caches from the
// measurement-type updates (lawn-care → count, cleaning → hourly).
const LIB_CACHE_KEY = 'qs-quote-library-cache-v3';

const readCache = () => {
  try { return JSON.parse(localStorage.getItem(LIB_CACHE_KEY) || 'null'); }
  catch { return null; }
};

export const useQuoteLibrary = () => {
  const cached = readCache();
  const [projectTypes, setProjectTypes] = useState(
    cached?.project_types?.length ? cached.project_types.map(normalisePT) : FALLBACK_PROJECT_TYPES,
  );
  const [addOns, setAddOns] = useState(
    cached?.add_ons?.length ? cached.add_ons.map(normaliseAO) : FALLBACK_ADD_ONS,
  );

  const refreshLibrary = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/quote-library`, { withCredentials: true });
      const pts = (data.project_types || []).map(normalisePT);
      const aos = (data.add_ons || []).map(normaliseAO);
      setProjectTypes(pts.length ? pts : FALLBACK_PROJECT_TYPES);
      setAddOns(aos.length ? aos : FALLBACK_ADD_ONS);
      try {
        localStorage.setItem(LIB_CACHE_KEY, JSON.stringify({
          project_types: data.project_types || [],
          add_ons: data.add_ons || [],
        }));
      } catch { /* quota — ignore */ }
    } catch {
      // unauthenticated or offline — fallback stays in place
    }
  }, []);

  useEffect(() => { refreshLibrary(); }, [refreshLibrary]);

  return { projectTypes, setProjectTypes, addOns, setAddOns, refreshLibrary };
};
